/*
 * Purpose: Stripe subscription checkout — sessions with promotion-code entry
 * Last modified: 2026-07-22 | Agent
 * Completeness: 98
 */
import { useCallback, useEffect, useState } from 'react';
import { Tag } from 'lucide-react';
import { loadStripePricingTableScript, resetStripePricingTableScript } from '@/components/billing/loadStripePricingTable';
import { ContextualError } from '@/components/errors/ContextualError';
import { useResolvedTheme } from '@/components/theme-provider';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { apiUrl } from '@/lib/apiBase';
import { PRICING_PLANS, type PricingPlanId } from '@/lib/pricingPlans';
import { toastContextualIssue } from '@/lib/contextualIssuePolicy';
import {
  getCheckoutPrice,
  resolveCheckoutCurrency,
  type StripeBillingInterval,
} from '@/lib/stripePrices';
import {
  getStripePricingTableId,
  getStripePublishableKey,
  isStripePricingConfigured,
  isStripePromotionCodesEnabled,
} from '@/lib/stripeConfig';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

type BillingStatus = { configured: boolean } | null;

async function startCheckout(priceId: string, customerEmail?: string | null) {
  const res = await fetch(apiUrl('/api/billing/checkout'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      priceId,
      ...(customerEmail ? { customerEmail } : {}),
    }),
  });
  const data = (await res.json().catch(() => ({}))) as {
    ok?: boolean;
    url?: string;
    error?: string;
  };
  if (!res.ok || !data.url) {
    throw new Error(data.error || 'Could not start Stripe checkout');
  }
  window.location.assign(data.url);
}

/** Coupon-aware Checkout Sessions; falls back to Pricing Table embed if API lacks Stripe. */
export function StripePricingTable() {
  const [interval, setInterval] = useState<StripeBillingInterval>('year');
  const [busyPlan, setBusyPlan] = useState<PricingPlanId | null>(null);
  const [billingStatus, setBillingStatus] = useState<BillingStatus>(null);
  const [embedReady, setEmbedReady] = useState(false);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const resolvedTheme = useResolvedTheme();

  const pricingTableId = getStripePricingTableId();
  const publishableKey = getStripePublishableKey();
  const embedConfigured = isStripePricingConfigured();
  const customerEmail = useAuthStore((s) => s.email);
  const currency = resolveCheckoutCurrency();
  const promoCodesEnabled = isStripePromotionCodesEnabled();

  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl('/api/billing/checkout/status'), { credentials: 'include' })
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as { configured?: boolean };
        if (!cancelled) setBillingStatus({ configured: Boolean(data.configured) });
      })
      .catch(() => {
        if (!cancelled) setBillingStatus({ configured: false });
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const loadEmbed = useCallback(() => {
    setEmbedError(null);
    setEmbedReady(false);
    resetStripePricingTableScript();
    return loadStripePricingTableScript()
      .then(() => setEmbedReady(true))
      .catch((e: unknown) => {
        setEmbedError(e instanceof Error ? e.message : 'Could not load Stripe pricing table');
      });
  }, []);

  const useSessionCheckout = billingStatus?.configured === true;
  const useEmbedFallback = billingStatus?.configured === false && embedConfigured;

  useEffect(() => {
    if (!useEmbedFallback) return;
    let cancelled = false;
    loadEmbed().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
  }, [useEmbedFallback, loadEmbed, reloadKey, resolvedTheme]);

  async function onCheckout(planId: PricingPlanId) {
    const price = getCheckoutPrice(planId, interval, currency);
    if (!price) {
      toastContextualIssue('Price not available for this region', {
        label: 'Retry',
        onClick: () => setReloadKey((k) => k + 1),
      });
      return;
    }
    setBusyPlan(planId);
    try {
      await startCheckout(price.priceId, customerEmail);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Checkout failed';
      toastContextualIssue(message, {
        label: 'Retry',
        onClick: () => void onCheckout(planId),
      });
      setBusyPlan(null);
    }
  }

  if (billingStatus === null) {
    return (
      <div className="pricing-stripe-shell w-full max-w-5xl mx-auto">
        <div className="pricing-stripe-inner flex items-center justify-center text-sm text-muted-foreground">
          Loading checkout…
        </div>
      </div>
    );
  }

  if (useSessionCheckout) {
    return (
      <div className="w-full max-w-5xl mx-auto space-y-4">
        {promoCodesEnabled ? (
          <p className="flex items-center justify-center gap-2 text-xs sm:text-sm text-muted-foreground text-center px-2">
            <Tag className="h-3.5 w-3.5 shrink-0 text-[var(--auth-accent)]" aria-hidden />
            Have a coupon? Enter your promotion code on the Stripe checkout page.
          </p>
        ) : null}

        <div
          className="flex justify-center"
          role="group"
          aria-label="Billing interval"
        >
          <div className="inline-flex rounded-lg border border-border/70 bg-muted/40 p-1 gap-1">
            <Button
              type="button"
              size="sm"
              variant={interval === 'month' ? 'default' : 'ghost'}
              actionTier={interval === 'month' ? 'secondary' : 'ghost'}
              className="min-w-[5.5rem]"
              onClick={() => setInterval('month')}
            >
              Monthly
            </Button>
            <Button
              type="button"
              size="sm"
              variant={interval === 'year' ? 'default' : 'ghost'}
              actionTier={interval === 'year' ? 'primary' : 'ghost'}
              className="min-w-[5.5rem]"
              onClick={() => setInterval('year')}
            >
              Yearly
              <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                −17%
              </Badge>
            </Button>
          </div>
        </div>

        <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
          {PRICING_PLANS.map((plan) => {
            const price = getCheckoutPrice(plan.id, interval, currency);
            return (
              <div
                key={plan.id}
                className={cn(
                  'rounded-xl border border-border/70 bg-card/95 p-5 sm:p-6 flex flex-col gap-4',
                  plan.highlighted &&
                    'ring-1 ring-[hsl(var(--ab-green-sage))] border-[hsl(var(--ab-green-sage))]/60'
                )}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{plan.name}</h3>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {plan.tagline}
                    </p>
                  </div>
                  {plan.highlighted ? (
                    <Badge className="bg-[hsl(var(--ab-green-core))] text-[hsl(var(--ab-ink-50))] shrink-0">
                      Popular
                    </Badge>
                  ) : null}
                </div>
                <p className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground">
                  {price?.label ?? '—'}
                </p>
                <Button
                  type="button"
                  actionTier={plan.highlighted ? 'primary' : 'secondary'}
                  className="w-full"
                  disabled={busyPlan != null || !price}
                  onClick={() => void onCheckout(plan.id)}
                >
                  {busyPlan === plan.id ? 'Redirecting…' : `Subscribe to ${plan.name}`}
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (!embedConfigured) {
    return (
      <ContextualError
        message="Stripe checkout is not configured. Set STRIPE_SECRET_KEY on the API (preferred) or VITE_STRIPE_PRICING_TABLE for the embed fallback."
        action={{
          label: 'Retry',
          onClick: () => setReloadKey((k) => k + 1),
        }}
        secondaryAction={{
          label: 'Support',
          href: '/help',
        }}
        className="max-w-2xl mx-auto"
      />
    );
  }

  if (embedError) {
    return (
      <ContextualError
        message={embedError}
        action={{
          label: 'Retry load',
          onClick: () => setReloadKey((k) => k + 1),
        }}
        className="max-w-2xl mx-auto"
      />
    );
  }

  const shellClass = cn(
    'pricing-stripe-shell w-full max-w-5xl mx-auto',
    resolvedTheme === 'dark' ? 'pricing-stripe-shell--dark' : 'pricing-stripe-shell--light'
  );

  if (!embedReady) {
    return (
      <div className={shellClass}>
        <div className="pricing-stripe-inner flex items-center justify-center text-sm text-muted-foreground">
          Loading checkout…
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto space-y-3">
      <ContextualError
        message="Coupon codes need API checkout (STRIPE_SECRET_KEY). Showing Pricing Table embed without promo entry until the API is configured."
        action={{
          label: 'Retry status',
          onClick: () => setReloadKey((k) => k + 1),
        }}
        secondaryAction={{ label: 'Support', href: '/help' }}
        className="max-w-2xl mx-auto"
      />
      <div className={shellClass}>
        <div className="pricing-stripe-inner">
          <stripe-pricing-table
            key={`${pricingTableId}-${resolvedTheme}-${reloadKey}`}
            pricing-table-id={pricingTableId}
            publishable-key={publishableKey}
            {...(customerEmail ? { 'customer-email': customerEmail } : {})}
          />
        </div>
      </div>
    </div>
  );
}
